import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  RISK_ASSESSMENT_ACCIDENT_CLASSIFICATIONS,
} from "@/lib/riskAssessmentAccidentClassification";
import { normalizeRiskAssessmentListValue } from "./riskAssessmentListValues";
import type { ProjectSourceReference } from "@/lib/projectSourceReferences";

type ArtifactImg = { kind: 'url'; url: string } | { kind: 'file'; fileId: string };
type RiskFrequencySeverityMethod = '3x3' | '4x3' | '5x4';
type RiskMethodType = '빈도강도법' | '3단계 판단법' | '체크리스트법';
const RISK_FREQUENCY_SEVERITY_METHODS: RiskFrequencySeverityMethod[] = ['3x3', '4x3', '5x4'];

const RISK_EVIDENCE_WARNING_REASONS: Record<string, string> = {
  SOURCE_DATA_DEGRADED: 'CSI 데이터 갱신 상태를 확인 중이며, 통계는 참고용으로 제공됩니다.',
  INSUFFICIENT_CSI_SAMPLE: '유사사고 표본이 부족해 통계 추천의 신뢰도가 낮습니다.',
  NEAR_MISS_REQUIRES_REVIEW: '아차사고 이력이 있어 현장 검토가 필요합니다.',
  AMBIGUOUS_LOST_TIME_DURATION: '휴업일수가 불명확해 강도 점수를 보수적으로 산정했습니다.',
  SITE_EVIDENCE_CONFLICT: '선택한 현장 피해수준과 상세 현장 이력이 서로 일치하지 않습니다.',
  UNDERSTATES_CSI_PRECEDENT: '현장 입력 점수가 CSI 유사사고 선례보다 낮습니다.',
  SHADOW_MODE: '통계 추천은 비교용이며 현재 점수에는 적용되지 않았습니다.',
  PROFILE_NOT_APPLIED: '현재 평가 방식은 CSI 5×4 자동 적용 대상이 아닙니다.',
  MODEL_FALLBACK: '통계 추천 대신 모델 추정 점수를 유지했습니다.',
  MISSING_HUMAN_ACCIDENT: '사고유형 정보가 없어 통계 매칭을 완료하지 못했습니다.',
  RISK_STATISTICS_UNAVAILABLE: 'CSI 통계 서비스를 조회하지 못했습니다.',
  BATCH_LIMIT_EXCEEDED: '한 번에 처리할 수 있는 통계 요청 건수를 초과했습니다.',
  NO_EXPOSURE_DENOMINATOR: '작업 노출량 분모가 없어 절대 발생률이 아닌 CSI 상대 빈도로 산정했습니다.',
  WORST_FATAL_PRECEDENT: '유사사고 표본에 사망사고 선례가 있어 최악 관측 강도를 반영했습니다.',
};

const riskEvidenceWarningReason = (warning: string): string =>
  RISK_EVIDENCE_WARNING_REASONS[warning] || `추가 검토 사유: ${warning}`;

// 통계 품질 한계와 실제 현장 주의 신호를 같은 경고로 보이지 않게 분리한다.
// 아래 항목만 색상 느낌표로 강조하고, 나머지는 중립적인 근거 안내로 표시한다.
const RISK_EVIDENCE_ATTENTION_WARNINGS = new Set([
  'NEAR_MISS_REQUIRES_REVIEW',
  'SITE_EVIDENCE_CONFLICT',
  'UNDERSTATES_CSI_PRECEDENT',
  'WORST_FATAL_PRECEDENT',
]);

const RISK_EVIDENCE_METHOD_LABELS: Record<string, string> = {
  RELATIVE_PREVALENCE_PERCENTILE: 'CSI 상대 발생빈도 백분위',
  SMOOTHED_RELATIVE_PREVALENCE_PERCENTILE: '표본 보정 상대 발생빈도 백분위',
  SITE_FREQUENCY_BAND: '현장 예상 발생주기',
  SITE_ACCIDENT_HISTORY_RECURRENCE: '현장 관측 사고 간격',
  SITE_OBSERVED_INTERVAL: '현장 관측 사고 간격',
  WORST_OBSERVED_SEVERITY: '최악 관측 강도',
  SMOOTHED_FATAL_RATE: '표본 보정 사망사고율',
  SMOOTHED_FATAL_RATE_AND_INJURY_EVIDENCE: '사망사고율 보정 + 상해 근거',
  SITE_SEVERITY_BAND: '현장 예상 피해수준',
  SITE_FATALITY_HISTORY: '현장 사망사고 이력',
  SITE_LOST_WORKDAY_HISTORY: '현장 휴업일수 이력',
  SITE_OBSERVED_SEVERITY: '현장 관측 피해수준',
};

const RISK_EVIDENCE_EXPOSURE_LABELS: Record<string, string> = {
  CSI_ACCIDENT_CASE_PEER_GROUPS_NO_EXPOSURE_DENOMINATOR: '작업 노출량 분모 없음·CSI 유사 그룹 간 상대 비교',
  CSI_CASE_SHARE_WITHIN_MATCHED_SCOPE: '동일 매칭 범위 내 CSI 사고 구성비',
  MATCHED_SCOPE_CASE_SHARE: '동일 매칭 범위 내 CSI 사고 구성비',
  NATIONAL_CSI_CASE_SHARE: '전국 CSI 사고 구성비',
  NO_EXPOSURE_DENOMINATOR: '작업 노출량 분모 없음',
};

const friendlyEvidenceValue = (
  value: string,
  labels: Record<string, string>,
): string => labels[value] || value.replace(/_/g, ' ');

const formatPercentile = (value: string): string => {
  if (!value) return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  const percent = numeric >= 0 && numeric <= 1 ? numeric * 100 : numeric;
  return `${percent.toLocaleString('ko-KR', { maximumFractionDigits: 2 })}%`;
};

const formatRate = (value: string): string => {
  if (!value) return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  const percent = numeric >= 0 && numeric <= 1 ? numeric * 100 : numeric;
  return `${percent.toLocaleString('ko-KR', { maximumFractionDigits: 2 })}%`;
};

const decodeXmlText = (value: string): string =>
  value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');

const formatEnforcementDate = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 8) return value;
  return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6, 8)} 시행`;
};

type LegalBasisLawDetail = {
  lawName: string;
  lawType: string;
  ministry: string;
  enforcementDate: string;
  articleNumber: string;
  articleTitle: string;
  articleContent: string;
  contentTruncated: boolean;
};

function BulletListEditor({ items, onChange }: { items: string[]; onChange: (v: string[]) => void }) {
  const move = (from: number, to: number) => {
    const next = [...items]; const [el] = next.splice(from, 1); next.splice(to, 0, el); onChange(next);
  };
  return (
    <div className="flex flex-col gap-1 min-w-0">
      {items.map((val, i) => (
        <div key={i} className="flex items-center gap-1">
          <input
            type="text"
            className="flex-1 min-w-0 text-[12px] bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 outline-none focus:border-seexr-primary"
            value={val}
            onChange={e => { const n = [...items]; n[i] = e.target.value; onChange(n); }}
            placeholder="대책 항목"
          />
          <div className="flex flex-col shrink-0">
            <button type="button" disabled={i === 0} onClick={() => move(i, i - 1)}
              className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-20 transition-opacity">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
            </button>
            <button type="button" disabled={i === items.length - 1} onClick={() => move(i, i + 1)}
              className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-20 transition-opacity">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
          </div>
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}
            className="shrink-0 p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, ''])}
        className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold text-seexr-primary hover:text-seexr-primary/80 transition-colors">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        항목 추가
      </button>
    </div>
  );
}

function LegalBasisIndicator({
  basis,
  verificationStatus,
  laws,
  unresolvedReferences,
}: {
  basis: string;
  verificationStatus: string;
  laws: LegalBasisLawDetail[];
  unresolvedReferences: string[];
}) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);
  const openTooltip = useCallback(() => {
    cancelClose();
    if (buttonRef.current) setAnchorRect(buttonRef.current.getBoundingClientRect());
  }, [cancelClose]);
  const closeTooltip = useCallback(() => {
    cancelClose();
    setAnchorRect(null);
  }, [cancelClose]);
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setAnchorRect(null), 100);
  }, [cancelClose]);

  useEffect(() => {
    if (!anchorRect) return;
    const update = () => {
      if (buttonRef.current) setAnchorRect(buttonRef.current.getBoundingClientRect());
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchorRect]);

  useEffect(() => () => cancelClose(), [cancelClose]);

  const tooltipWidth = 384;
  const tooltipLeft = anchorRect
    ? Math.min(
        Math.max(anchorRect.left + anchorRect.width / 2, tooltipWidth / 2 + 12),
        window.innerWidth - tooltipWidth / 2 - 12,
      )
    : 0;
  const showBelow = !!anchorRect && anchorRect.top < window.innerHeight / 2;
  const verified = laws.length > 0;

  return (
    <span className="ml-1.5 inline-flex shrink-0 align-middle">
      <button
        ref={buttonRef}
        type="button"
        aria-label={`${basis} 관련 법령 상세정보`}
        aria-expanded={Boolean(anchorRect)}
        onMouseEnter={openTooltip}
        onMouseLeave={scheduleClose}
        onFocus={openTooltip}
        onBlur={scheduleClose}
        onClick={() => anchorRect ? closeTooltip() : openTooltip()}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            closeTooltip();
            event.currentTarget.blur();
          }
        }}
        className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-gray-300 bg-gray-50 text-gray-500 outline-none transition-colors hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1 dark:border-gray-600 dark:bg-gray-800/40 dark:text-gray-400 dark:hover:bg-gray-800/70"
      >
        <svg aria-hidden="true" width="10" height="10" viewBox="0 0 12 12" fill="none">
          <path d="M4.7 4.6a1.35 1.35 0 1 1 2.15 1.09c-.55.4-.85.68-.85 1.31M6 8.9h.01" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" />
        </svg>
      </button>
      {anchorRect && typeof document !== 'undefined' && createPortal(
        <div
          role="tooltip"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          className="fixed w-96 max-w-[calc(100vw-24px)] overflow-y-auto whitespace-normal rounded-xl border border-gray-200 bg-white p-3.5 text-left text-[11px] font-medium leading-relaxed text-gray-700 opacity-100 shadow-2xl dark:border-gray-700 dark:bg-[#202023] dark:text-gray-200"
          style={{
            left: tooltipLeft,
            top: showBelow ? anchorRect.bottom + 8 : anchorRect.top - 8,
            maxHeight: 'min(420px, calc(100vh - 24px))',
            transform: showBelow ? 'translateX(-50%)' : 'translate(-50%, -100%)',
            zIndex: 2147483647,
          }}
        >
          <span className="flex items-center justify-between gap-3">
            <span className="font-black text-gray-900 dark:text-gray-100">관련 법령 상세</span>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-black ${verified ? 'bg-seexr-50 text-seexr-700 dark:bg-seexr-950/40 dark:text-seexr-300' : 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'}`}>
              {verified ? '검증필' : '상세 확인 필요'}
            </span>
          </span>
          <span className="mt-1.5 block text-[10px] text-gray-500 dark:text-gray-400">
            표기 근거: {basis}
          </span>
          {laws.map((law, index) => (
            <span key={`${law.lawName}-${law.articleNumber}-${index}`} className="mt-3 block border-t border-gray-100 pt-3 dark:border-gray-700">
              <span className="block font-black text-gray-900 dark:text-gray-100">
                {law.lawName} 제{law.articleNumber}조{law.articleTitle ? `(${law.articleTitle})` : ''}
              </span>
              <span className="mt-0.5 block text-[10px] text-gray-500 dark:text-gray-400">
                {[law.lawType, law.ministry, formatEnforcementDate(law.enforcementDate)].filter(Boolean).join(' · ')}
              </span>
              <span className="mt-2 block whitespace-pre-wrap leading-[1.65] text-gray-700 dark:text-gray-200">
                {law.articleContent || '조문 상세 내용이 비어 있습니다.'}
              </span>
              {law.contentTruncated && (
                <span className="mt-1 block text-[10px] text-gray-400">긴 조문은 위험성평가표 표시 범위에 맞게 일부 생략되었습니다.</span>
              )}
            </span>
          ))}
          {unresolvedReferences.length > 0 && (
            <span className="mt-3 block border-t border-amber-100 pt-2.5 text-amber-700 dark:border-amber-900/60 dark:text-amber-300">
              <span className="block font-bold">법령 DB에서 정확한 조문을 확인하지 못한 근거</span>
              {unresolvedReferences.map(reference => (
                <span key={reference} className="mt-0.5 block">• {reference}</span>
              ))}
            </span>
          )}
          {!verified && unresolvedReferences.length === 0 && (
            <span className="mt-3 block border-t border-gray-100 pt-2.5 text-gray-500 dark:border-gray-700 dark:text-gray-400">
              법령 상세정보가 제공되지 않았습니다.
            </span>
          )}
        </div>,
        document.body,
      )}
    </span>
  );
}

function LegalBasisCell({
  basis,
  verificationStatus,
  laws,
  unresolvedReferences,
}: {
  basis: string;
  verificationStatus: string;
  laws: LegalBasisLawDetail[];
  unresolvedReferences: string[];
}) {
  const hasBasis = Boolean(basis.trim() && basis.trim() !== '-');
  return (
    <span className="inline-flex items-start">
      <span className="whitespace-pre-wrap">{basis}</span>
      {hasBasis && (
        <LegalBasisIndicator
          basis={basis}
          verificationStatus={verificationStatus}
          laws={laws}
          unresolvedReferences={unresolvedReferences}
        />
      )}
    </span>
  );
}

function RiskEvidenceIndicator({
  needsAttention,
  ariaLabel,
  scoreSourceLabel,
  recommendedFrequency,
  recommendedSeverity,
  recommendedRisk,
  sampleSize,
  confidence,
  matchedScope,
  windowStart,
  windowEnd,
  frequencyMethod,
  relativePrevalencePercentile,
  smoothedRelativePrevalencePercentile,
  exposureBasis,
  severityMethod,
  severityMinScore,
  severityMaxScore,
  worstObservedSeverity,
  fatalRate,
  smoothedFatalRate,
  reviewReasons,
}: {
  needsAttention: boolean;
  ariaLabel: string;
  scoreSourceLabel: string;
  recommendedFrequency: string;
  recommendedSeverity: string;
  recommendedRisk: string;
  sampleSize: string;
  confidence: string;
  matchedScope: string;
  windowStart: string;
  windowEnd: string;
  frequencyMethod: string;
  relativePrevalencePercentile: string;
  smoothedRelativePrevalencePercentile: string;
  exposureBasis: string;
  severityMethod: string;
  severityMinScore: string;
  severityMaxScore: string;
  worstObservedSeverity: string;
  fatalRate: string;
  smoothedFatalRate: string;
  reviewReasons: string[];
}) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const updateAnchorRect = useCallback(() => {
    if (buttonRef.current) setAnchorRect(buttonRef.current.getBoundingClientRect());
  }, []);

  useEffect(() => {
    if (!anchorRect) return;
    const update = () => updateAnchorRect();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchorRect, updateAnchorRect]);

  const tooltipWidth = 288;
  const tooltipLeft = anchorRect
    ? Math.min(
        Math.max(anchorRect.left + anchorRect.width / 2, tooltipWidth / 2 + 12),
        window.innerWidth - tooltipWidth / 2 - 12,
      )
    : 0;
  const showBelow = !!anchorRect && anchorRect.top < window.innerHeight / 2;

  return (
    <span className="ml-1 inline-flex align-middle">
      <button
        ref={buttonRef}
        type="button"
        aria-label={ariaLabel}
        onMouseEnter={updateAnchorRect}
        onMouseLeave={() => setAnchorRect(null)}
        onFocus={updateAnchorRect}
        onBlur={() => setAnchorRect(null)}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            setAnchorRect(null);
            event.currentTarget.blur();
          }
        }}
        className={`inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border outline-none transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 ${needsAttention ? 'border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100 focus-visible:ring-amber-400 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300 dark:hover:bg-amber-950/50' : 'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100 focus-visible:ring-gray-400 dark:border-gray-600 dark:bg-gray-800/40 dark:text-gray-400 dark:hover:bg-gray-800/70'}`}
      >
        <svg aria-hidden="true" width="10" height="10" viewBox="0 0 12 12" fill="none">
          {needsAttention ? (
            <path d="M6 3.1v3.5M6 8.7h.01" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          ) : (
            <path d="M4.7 4.6a1.35 1.35 0 1 1 2.15 1.09c-.55.4-.85.68-.85 1.31M6 8.9h.01" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" />
          )}
        </svg>
      </button>
      {anchorRect && typeof document !== 'undefined' && createPortal(
        <div
          role="tooltip"
          className={`pointer-events-none fixed w-72 whitespace-normal rounded-lg border p-3 text-left text-[11px] font-medium leading-relaxed text-gray-700 opacity-100 dark:bg-[#202023] dark:text-gray-200 ${needsAttention ? 'border-amber-200 bg-amber-50 shadow-2xl dark:border-amber-800' : 'border-gray-200 bg-white shadow-xl dark:border-gray-700'}`}
          style={{
            left: tooltipLeft,
            top: showBelow ? anchorRect.bottom + 8 : anchorRect.top - 8,
            maxWidth: 'calc(100vw - 24px)',
            transform: showBelow ? 'translateX(-50%)' : 'translate(-50%, -100%)',
            zIndex: 2147483647,
          }}
        >
          <span className={`mb-1.5 block font-black ${needsAttention ? 'text-amber-700 dark:text-amber-300' : 'text-gray-700 dark:text-gray-200'}`}>
            통계 산정 근거{needsAttention ? ' · 현장 확인 권장' : ''}
          </span>
          <span className="block">출처: {scoreSourceLabel}</span>
          {recommendedFrequency && recommendedSeverity && (
            <span className="block">추천: {recommendedFrequency} × {recommendedSeverity}{recommendedRisk ? ` = ${recommendedRisk}` : ''}</span>
          )}
          {sampleSize && <span className="block">표본: {sampleSize}건</span>}
          {confidence && <span className="block">신뢰도: {confidence}</span>}
          {matchedScope && <span className="block">매칭 범위: {matchedScope}</span>}
          {windowStart && windowEnd && <span className="block">통계 기간: {windowStart} ~ {windowEnd}</span>}
          {(frequencyMethod || relativePrevalencePercentile || smoothedRelativePrevalencePercentile || exposureBasis) && (
            <span className="mt-1.5 block border-t border-gray-100 pt-1.5 dark:border-gray-700">
              <span className="block font-bold text-gray-900 dark:text-gray-100">빈도 산정</span>
              {frequencyMethod && (
                <span className="block">방법: {friendlyEvidenceValue(frequencyMethod, RISK_EVIDENCE_METHOD_LABELS)}</span>
              )}
              {relativePrevalencePercentile && (
                <span className="block">상대 발생빈도 백분위: {formatPercentile(relativePrevalencePercentile)}</span>
              )}
              {smoothedRelativePrevalencePercentile && (
                <span className="block">표본 보정 백분위: {formatPercentile(smoothedRelativePrevalencePercentile)}</span>
              )}
              {exposureBasis && (
                <span className="block">노출 기준: {friendlyEvidenceValue(exposureBasis, RISK_EVIDENCE_EXPOSURE_LABELS)}</span>
              )}
            </span>
          )}
          {(severityMethod || severityMinScore || severityMaxScore || worstObservedSeverity || fatalRate || smoothedFatalRate) && (
            <span className="mt-1.5 block border-t border-gray-100 pt-1.5 dark:border-gray-700">
              <span className="block font-bold text-gray-900 dark:text-gray-100">강도 산정</span>
              {severityMethod && (
                <span className="block">방법: {friendlyEvidenceValue(severityMethod, RISK_EVIDENCE_METHOD_LABELS)}</span>
              )}
              {(severityMinScore || severityMaxScore) && (
                <span className="block">강도 범위: {severityMinScore || '?'} ~ {severityMaxScore || '?'}</span>
              )}
              {worstObservedSeverity && <span className="block">최악 관측 강도: {worstObservedSeverity}점</span>}
              {fatalRate && <span className="block">사망사고율: {formatRate(fatalRate)}</span>}
              {smoothedFatalRate && <span className="block">표본 보정 사망사고율: {formatRate(smoothedFatalRate)}</span>}
            </span>
          )}
          {reviewReasons.length > 0 && (
            <span className={`mt-1.5 block border-t pt-1.5 ${needsAttention ? 'border-amber-100 text-amber-800/80 dark:border-amber-900/60 dark:text-amber-200/80' : 'border-gray-100 text-gray-500 dark:border-gray-700 dark:text-gray-400'}`}>
              {reviewReasons.map(reason => (
                <span key={reason} className="block">• {reason}</span>
              ))}
            </span>
          )}
        </div>,
        document.body,
      )}
    </span>
  );
}

type RiskEditItem = {
  id: string;
  prob_freq: string;
  severity: string;
  hazard_primary_category: string;
  risk_category: string;
  accident_classification: string;
  hazard: string;
  risk_level: string;
  reduction_measures_detail: string[];
  post_improvement_prob_freq: string;
  post_improvement_severity: string;
  post_improvement_risk_level: string;
  basis: string;
  legal_basis_details: string;
  check_result: string;
  statistical_basis: string;
  recommended_prob_freq: string;
  recommended_severity: string;
  recommended_risk_level: string;
};

type XmlParser = {
  extract: (tag: string, source: string) => string;
  extractAll: (tag: string, source: string) => string[];
};

type SourceDocumentAttachment = {
  fileId: string;
  kind: 'source_document';
  name?: string;
  mimeType?: string;
  size?: number;
  format?: string;
  characterCount?: number;
};

export type ChatViewRiskAssessmentReportProps = {
  xmlContent: string;
  isEditing: boolean;
  riskMethodType: RiskMethodType;
  riskMethod: RiskFrequencySeverityMethod;
  setRiskMethod: React.Dispatch<React.SetStateAction<RiskFrequencySeverityMethod>>;
  showMethodPicker: boolean;
  setShowMethodPicker: React.Dispatch<React.SetStateAction<boolean>>;
  editItems: RiskEditItem[];
  setEditItems: React.Dispatch<React.SetStateAction<RiskEditItem[]>>;
  editEquipment: string[];
  setEditEquipment: React.Dispatch<React.SetStateAction<string[]>>;
  editMaterial: string[];
  setEditMaterial: React.Dispatch<React.SetStateAction<string[]>>;
  eqInput: string;
  setEqInput: React.Dispatch<React.SetStateAction<string>>;
  matInput: string;
  setMatInput: React.Dispatch<React.SetStateAction<string>>;
  editAttachmentFileIds: string[];
  dragOverIdx: number | null;
  parseXmlString: (xml: string) => XmlParser;
  getRiskAttachmentFileIds: (source: string) => string[];
  handleRiskFormSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  removeRiskAttachment: (index: number) => void;
  addRiskAttachmentsFromFileList: (files: FileList | null) => Promise<void>;
  setViewerState: React.Dispatch<React.SetStateAction<{ images: ArtifactImg[]; index: number } | null>>;
  handleDragStart: (index: number) => void;
  handleDragOver: (event: React.DragEvent, index: number) => void;
  handleDragEnd: () => void;
  updateEditItem: (index: number, field: string, value: string) => void;
  deleteItem: (index: number) => void;
  addItem: () => void;
  bulletLines: (text: string) => string;
  AuthImg: React.ComponentType<{ fileId: string; className?: string; alt?: string; onError?: () => void }>;
  sourceDocuments: SourceDocumentAttachment[];
  projectSourceReferences: ProjectSourceReference[];
  SourceDocumentCard: React.ComponentType<{ documentFile: SourceDocumentAttachment }>;
  renderRiskEducationAction?: (index: number) => React.ReactNode;
  artifactId?: string | number | null;
  sessionId?: string | null;
  messageId?: string | number | null;
  headerActionsTarget?: HTMLElement | null;
};

export function ChatViewRiskAssessmentReport({
  xmlContent,
  isEditing,
  riskMethodType,
  riskMethod,
  setRiskMethod,
  showMethodPicker,
  setShowMethodPicker,
  editItems,
  setEditItems,
  editEquipment,
  setEditEquipment,
  editMaterial,
  setEditMaterial,
  eqInput,
  setEqInput,
  matInput,
  setMatInput,
  editAttachmentFileIds,
  dragOverIdx,
  parseXmlString,
  getRiskAttachmentFileIds,
  handleRiskFormSubmit,
  removeRiskAttachment,
  addRiskAttachmentsFromFileList,
  setViewerState,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  updateEditItem,
  deleteItem,
  addItem,
  bulletLines,
  AuthImg,
  sourceDocuments,
  projectSourceReferences,
  SourceDocumentCard,
  renderRiskEducationAction,
}: ChatViewRiskAssessmentReportProps) {
  const renderRiskAssessment = () => {
    const { extract, extractAll } = parseXmlString(`<root>${xmlContent}</root>`);

    // Common Info
    const industryName = extract('industry_name', xmlContent);
    const processName = extract('process_name', xmlContent);
    const processDesc = extract('process_description', xmlContent);
    const equipment = normalizeRiskAssessmentListValue(
      extract('equipment', xmlContent),
    );
    const material = normalizeRiskAssessmentListValue(
      extract('material', xmlContent),
    );

    // 첨부 이미지 파일 ID: XML 값이 예시/플레이스홀더면 인접 메시지의 실제 업로드 ID로 보강
    const attachmentFileIds = getRiskAttachmentFileIds(xmlContent);
    const attachmentImgs: ArtifactImg[] = attachmentFileIds.map(fileId => ({ kind: 'file' as const, fileId }));

    // Items
    const itemsBlock = extract('items', xmlContent) || xmlContent;
    const items = extractAll('item', itemsBlock);

    if (!items || items.length === 0) {
      return (
        <div className="flex flex-col gap-6 w-full animate-pulse">
          <div className="bg-white dark:bg-[#161618] rounded-2xl p-6 border border-gray-200 dark:border-[#2a2a2e] shadow-sm">
            <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <div className="h-2.5 w-10 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="h-2.5 w-10 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="flex flex-col gap-1.5 col-span-2">
                <div className="h-2.5 w-12 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="h-2.5 w-14 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="h-2.5 w-8 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            </div>
          </div>
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white dark:bg-[#161618] rounded-2xl p-6 border border-gray-200 dark:border-[#2a2a2e] shadow-sm flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded-full shrink-0 mt-0.5" />
                <div className="flex-1 flex flex-col gap-2">
                  <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-700 rounded" />
                </div>
                <div className="h-6 w-12 bg-gray-200 dark:bg-gray-700 rounded-md shrink-0" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3].map(j => (
                  <div key={j} className="bg-gray-50 dark:bg-[#1f1f22] rounded-xl p-3 flex flex-col items-center gap-1.5">
                    <div className="h-2.5 w-8 bg-gray-200 dark:bg-gray-700 rounded" />
                    <div className="h-6 w-8 bg-gray-200 dark:bg-gray-700 rounded" />
                  </div>
                ))}
              </div>
              <div className="bg-gray-50 dark:bg-[#1f1f22] rounded-xl p-4 flex flex-col gap-2">
                <div className="h-2.5 w-16 bg-gray-200 dark:bg-gray-700 rounded mb-1" />
                <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-3 w-5/6 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    // ── Color constants ────────────────────────────────────────
    const C = {
      lowest:   { bg: "bg-seexr-100 dark:bg-seexr-900/40",     text: "text-seexr-600 dark:text-seexr-400" },
      low:      { bg: "bg-green-100 dark:bg-green-900/40",   text: "text-green-600 dark:text-green-400" },
      caution:  { bg: "bg-yellow-100 dark:bg-yellow-900/40", text: "text-amber-500 dark:text-amber-400" },
      elevated: { bg: "bg-orange-100 dark:bg-orange-900/40", text: "text-orange-500 dark:text-orange-400" },
      high:     { bg: "bg-orange-200 dark:bg-orange-800/50", text: "text-orange-700 dark:text-orange-300" },
      critical: { bg: "bg-red-100 dark:bg-red-900/40",       text: "text-red-500 dark:text-red-400" },
      neutral:  { bg: "bg-white dark:bg-[#161618]",           text: "text-[#505a69] dark:text-gray-300" },
    };

    // ── 빈도 색상 (Frequency) ────────────────────────────────
    const getFreqColor = (valStr: string) => {
      if (!valStr.trim()) return C.neutral;
      const v = Number(valStr);
      if (isNaN(v)) return C.neutral;
      if (riskMethod === '3x3') {
        if (v <= 1) return C.low;
        if (v <= 2) return C.caution;
        return C.critical;
      }
      if (riskMethod === '4x3') {
        if (v <= 1) return C.low;
        if (v <= 2) return C.caution;
        if (v <= 3) return C.elevated;
        return C.critical;
      }
      if (v <= 1) return C.lowest;
      if (v <= 2) return C.low;
      if (v <= 3) return C.caution;
      if (v <= 4) return C.elevated;
      return C.critical;
    };

    // ── 강도 색상 (Severity) ─────────────────────────────────
    const getSevColor = (valStr: string) => {
      if (!valStr.trim()) return C.neutral;
      const v = Number(valStr);
      if (isNaN(v)) return C.neutral;
      if (riskMethod === '3x3') {
        if (v <= 1) return C.low;
        if (v <= 2) return C.caution;
        return C.critical;
      }
      if (riskMethod === '4x3') {
        if (v <= 1) return C.low;
        if (v <= 2) return C.elevated;
        return C.critical;
      }
      if (v <= 1) return C.lowest;
      if (v <= 2) return C.low;
      if (v <= 3) return C.elevated;
      return C.critical;
    };

    // ── 위험성 등급 색상 (Risk Level) ────────────────────────
    const getRiskColor = (valStr: string) => {
      if (!valStr.trim()) return C.neutral;
      const v = Number(valStr);
      if (isNaN(v)) {
        if (valStr === "하" || valStr === "낮음") return C.low;
        if (valStr === "중" || valStr === "보통") return C.caution;
        if (valStr === "상" || valStr === "높음") return C.critical;
        return C.neutral;
      }
      if (riskMethod === '3x3') {
        if (v < 3) return C.low;
        if (v < 6) return C.caution;
        return C.critical;
      }
      if (riskMethod === '4x3') {
        if (v < 4) return C.low;
        if (v < 7) return C.caution;
        if (v < 10) return C.elevated;
        return C.critical;
      }
      if (v < 4) return C.lowest;
      if (v < 7) return C.low;
      if (v < 9) return C.caution;
      if (v < 13) return C.elevated;
      if (v < 16) return C.high;
      return C.critical;
    };
    
    return (
      <form id="risk-form" onSubmit={handleRiskFormSubmit} className="flex flex-col gap-6 w-full">
        {/* Common Header Info */}
        <div className="bg-white dark:bg-[#161618] rounded-2xl p-6 border border-gray-200 dark:border-[#2a2a2e] shadow-sm">
           <h2 className="text-xl font-black text-gray-900 dark:text-white mb-4">작업 개요</h2>
           <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                 <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">업종명</span>
                 {isEditing ? (
                    <input type="text" name="industry_name" defaultValue={industryName || ''} className="text-sm border border-gray-300 dark:border-[#3a3a3e] rounded-md px-2 py-1.5 focus:outline-none focus:border-seexr-primary bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 font-semibold" placeholder="업종명 입력" />
                 ) : (
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{industryName || '-'}</span>
                 )}
              </div>
              <div className="flex flex-col">
                 <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">공종명</span>
                 {isEditing ? (
                    <input type="text" name="process_name" defaultValue={processName || ''} className="text-sm border border-gray-300 dark:border-[#3a3a3e] rounded-md px-2 py-1.5 focus:outline-none focus:border-seexr-primary bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 font-semibold" placeholder="공종명 입력" />
                 ) : (
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{processName || '-'}</span>
                 )}
              </div>
              <div className="flex flex-col col-span-2">
                 <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">공종설명</span>
                 {isEditing ? (
                    <textarea name="process_description" defaultValue={processDesc || ''} rows={2} className="text-sm border border-gray-300 dark:border-[#3a3a3e] rounded-md px-2 py-1.5 focus:outline-none focus:border-seexr-primary bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 font-semibold resize-none" placeholder="공종설명 입력" />
                 ) : (
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{processDesc || '-'}</span>
                 )}
              </div>
              <div className="flex flex-col">
                 <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">장비/설비</span>
                 {isEditing ? (
                    <div className="flex flex-wrap items-center gap-1.5 min-h-[34px] border border-gray-300 dark:border-[#3a3a3e] rounded-md px-2 py-1.5 focus-within:border-seexr-primary transition-colors">
                       {editEquipment.map((tag, i) => (
                         <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-seexr-100 dark:bg-seexr-900/40 text-seexr-700 dark:text-seexr-300 text-xs font-bold">
                           {tag}
                           <button type="button" onClick={() => setEditEquipment(prev => prev.filter((_, j) => j !== i))} className="hover:text-red-500 dark:hover:text-red-400 transition-colors ml-0.5">
                             <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                           </button>
                         </span>
                       ))}
                       <input
                         type="text"
                         value={eqInput}
                         onChange={e => setEqInput(e.target.value)}
                         onKeyDown={e => {
                           if ((e.key === 'Enter' || e.key === ',') && eqInput.trim()) {
                             e.preventDefault();
                             setEditEquipment(prev => [...prev, eqInput.trim()]);
                             setEqInput('');
                           } else if (e.key === 'Backspace' && !eqInput && editEquipment.length > 0) {
                             setEditEquipment(prev => prev.slice(0, -1));
                           }
                         }}
                         className="flex-1 min-w-[80px] text-sm bg-transparent outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 font-semibold"
                         placeholder={editEquipment.length === 0 ? "장비/설비 입력 후 Enter" : ""}
                       />
                    </div>
                 ) : (
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{equipment || '-'}</span>
                 )}
              </div>
              <div className="flex flex-col">
                 <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">물질</span>
                 {isEditing ? (
                    <div className="flex flex-wrap items-center gap-1.5 min-h-[34px] border border-gray-300 dark:border-[#3a3a3e] rounded-md px-2 py-1.5 focus-within:border-seexr-primary transition-colors">
                       {editMaterial.map((tag, i) => (
                         <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 text-xs font-bold">
                           {tag}
                           <button type="button" onClick={() => setEditMaterial(prev => prev.filter((_, j) => j !== i))} className="hover:text-red-500 dark:hover:text-red-400 transition-colors ml-0.5">
                             <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                           </button>
                         </span>
                       ))}
                       <input
                         type="text"
                         value={matInput}
                         onChange={e => setMatInput(e.target.value)}
                         onKeyDown={e => {
                           if ((e.key === 'Enter' || e.key === ',') && matInput.trim()) {
                             e.preventDefault();
                             setEditMaterial(prev => [...prev, matInput.trim()]);
                             setMatInput('');
                           } else if (e.key === 'Backspace' && !matInput && editMaterial.length > 0) {
                             setEditMaterial(prev => prev.slice(0, -1));
                           }
                         }}
                         className="flex-1 min-w-[80px] text-sm bg-transparent outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 font-semibold"
                         placeholder={editMaterial.length === 0 ? "물질 입력 후 Enter" : ""}
                       />
                    </div>
                 ) : (
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{material || '-'}</span>
                 )}
              </div>
           </div>

           {/* 현장 사진: 사용자가 업로드한 이미지 — 읽기 모드는 뷰어 오픈, 편집 모드는 X 삭제 + 추가 버튼 */}
           {(isEditing ? true : attachmentImgs.length > 0) && (
              <div className="mt-5 pt-5 border-t border-gray-100 dark:border-[#2a2a2e]">
                 <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">현장 사진</span>
                    <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500">
                      {(isEditing ? editAttachmentFileIds.length : attachmentImgs.length)}장
                    </span>
                 </div>
                 <div className="flex gap-3 overflow-x-auto custom-scrollbar pb-1">
                    {isEditing ? (
                      <>
                        {editAttachmentFileIds.map((fileId, i) => (
                          <div key={fileId} className="shrink-0 h-24 w-32 rounded-xl overflow-hidden border border-gray-200 dark:border-[#2a2a2e] shadow-sm relative">
                            <AuthImg fileId={fileId} className="h-full w-full object-cover" alt="현장 사진" />
                            <button
                              type="button"
                              onClick={() => removeRiskAttachment(i)}
                              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center shadow-md transition-colors"
                              aria-label="사진 삭제"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                        ))}
                        <label className="shrink-0 h-24 w-24 rounded-xl border-2 border-dashed border-gray-300 dark:border-[#3a3a3e] flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-seexr-primary hover:bg-seexr-solid/5 transition-colors text-gray-400 hover:text-seexr-primary">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                          <span className="text-[10px] font-bold">사진 추가</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => { addRiskAttachmentsFromFileList(e.target.files); e.target.value = ''; }}
                          />
                        </label>
                      </>
                    ) : (
                      attachmentImgs.map((img, i) => (
                         <button
                            type="button"
                            key={img.kind === 'file' ? img.fileId : `att-${i}`}
                            onClick={() => setViewerState({ images: attachmentImgs, index: i })}
                            className="shrink-0 h-24 w-32 rounded-xl overflow-hidden border border-gray-200 dark:border-[#2a2a2e] shadow-sm cursor-zoom-in group relative"
                         >
                            <AuthImg
                               fileId={img.kind === 'file' ? img.fileId : ''}
                               className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                               alt="현장 사진"
                            />
                         </button>
                      ))
                    )}
                 </div>
              </div>
           )}
        </div>

        {/* Risk method badges */}
        <div className="flex items-center gap-2 mt-1 mb-1">
          {/* Fixed badge: method type */}
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-black border bg-gray-100 dark:bg-[#1f1f22] text-gray-500 dark:text-gray-400 border-gray-200 dark:border-[#2a2a2e]">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
            {riskMethodType}
          </span>
          {/* Editable badge: 위험성추정 (only for 빈도강도법) */}
          {riskMethodType === '빈도강도법' && (
            <div className="relative">
              <button
                type="button"
                onClick={(e) => { if (!isEditing) return; e.stopPropagation(); setShowMethodPicker(prev => !prev); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-black border transition-colors ${isEditing ? 'bg-seexr-solid/10 text-seexr-primary hover:bg-seexr-solid/20 border-seexr-primary/20 cursor-pointer' : 'bg-gray-100 dark:bg-[#1f1f22] text-gray-500 dark:text-gray-400 border-gray-200 dark:border-[#2a2a2e] cursor-default'}`}
              >
                {riskMethod} 위험성추정
                {isEditing && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>}
              </button>
              {isEditing && showMethodPicker && (
                <div className="absolute top-full left-0 mt-1 z-20 bg-white dark:bg-[#1e1e21] rounded-xl border border-gray-200 dark:border-[#2a2a2e] shadow-xl overflow-hidden min-w-[180px]">
                  {RISK_FREQUENCY_SEVERITY_METHODS.map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => { setRiskMethod(m); setShowMethodPicker(false); }}
                      className={`w-full px-4 py-2.5 text-left text-[13px] font-bold flex items-center justify-between transition-colors ${riskMethod === m ? 'bg-seexr-solid/10 text-seexr-primary' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#2a2a2e]'}`}
                    >
                      <span>{m} 위험성추정</span>
                      {riskMethod === m && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Dynamic Display of Items based on available tags */}
        <div className="w-full bg-white dark:bg-[#161618] rounded-xl border border-gray-200 dark:border-[#2a2a2e] shadow-sm overflow-hidden flex flex-col">
           <div className="overflow-x-auto custom-scrollbar w-full">
             <table className={`w-full text-left border-collapse ${riskMethodType === '빈도강도법' ? 'min-w-[1350px]' : riskMethodType === '체크리스트법' ? 'min-w-[990px]' : 'min-w-[830px]'}`}>
               <thead className="sticky top-0 z-10 backdrop-blur-xl bg-white/90 dark:bg-[#161618]/90">
                 {riskMethodType === '빈도강도법' ? (
                   <>
                     <tr className="border-b border-seexr-primary/20 dark:border-seexr-primary/20 text-center bg-seexr-solid/5 dark:bg-seexr-solid/10">
                       {isEditing && <th rowSpan={2} className="w-10 border-r border-seexr-primary/20 dark:border-seexr-primary/20" />}
                       <th colSpan={5} className="py-2.5 border-r border-seexr-primary/20 dark:border-seexr-primary/20 text-seexr-primary font-black text-[13px] tracking-widest uppercase">분석 기반 정보</th>
                       <th colSpan={3} className="py-2.5 border-r border-seexr-primary/20 dark:border-seexr-primary/20 text-seexr-primary font-black text-[13px] tracking-widest uppercase">개선 전 위험성 수준</th>
                       <th rowSpan={2} className="py-3 min-w-[240px] text-left pl-6 border-r border-seexr-primary/20 dark:border-seexr-primary/20 text-seexr-primary font-black text-[12px]">위험방지대책</th>
                       <th colSpan={3} className="py-2.5 border-r border-seexr-primary/20 dark:border-seexr-primary/20 text-seexr-primary font-black text-[12px] tracking-wider">개선 후 위험성 수준</th>
                       <th rowSpan={2} className="py-3 min-w-[160px] text-left pl-6 text-seexr-primary font-black text-[12px]">관련근거</th>
                       {isEditing && <th rowSpan={2} className="w-10 border-l border-seexr-primary/20 dark:border-seexr-primary/20" />}
                     </tr>
                     <tr className="border-b border-gray-200 dark:border-[#2a2a2e] text-gray-700 dark:text-gray-300 text-[12px] font-black text-center bg-seexr-solid/5 dark:bg-seexr-solid/[0.02]">
                       <th className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] w-12 shrink-0">No.</th>
                       <th className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] w-24">공종분류</th>
                       <th className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] w-28">단위작업</th>
                       <th className="w-28 min-w-28 max-w-28 py-3 border-r border-gray-100 dark:border-[#2a2a2e]">사고분류</th>
                       <th className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] min-w-[200px]">위험요인</th>
                       <th className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] w-14 shrink-0 px-1">빈도</th>
                       <th className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] w-14 shrink-0 px-1">강도</th>
                       <th className="py-3 border-r border-seexr-primary/20 dark:border-seexr-primary/20 w-[66px] shrink-0">위험도</th>
                       <th className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] w-14 shrink-0 px-1">빈도</th>
                       <th className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] w-14 shrink-0 px-1">강도</th>
                       <th className="py-3 border-r border-seexr-primary/20 dark:border-seexr-primary/20 w-[66px] shrink-0">위험도</th>
                     </tr>
                   </>
                 ) : riskMethodType === '3단계 판단법' ? (
                   <tr className="border-b border-gray-200 dark:border-[#2a2a2e] text-gray-700 dark:text-gray-300 text-[12px] font-black text-center bg-seexr-solid/5 dark:bg-seexr-solid/10">
                     {isEditing && <th className="w-10 border-r border-seexr-primary/20 dark:border-seexr-primary/20" />}
                     <th className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] w-12 shrink-0">No.</th>
                     <th className="w-28 min-w-28 max-w-28 py-3 border-r border-gray-100 dark:border-[#2a2a2e]">사고분류</th>
                     <th className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] min-w-[240px]">유해위험요인</th>
                     <th className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] w-[96px] min-w-[96px] max-w-[96px]">개선 전 위험성 수준</th>
                     <th className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] min-w-[200px]">개선대책</th>
                     <th className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] w-[96px] min-w-[96px] max-w-[96px]">개선 후 위험성 수준</th>
                     <th className="py-3 min-w-[140px] text-left pl-6">관련근거</th>
                     {isEditing && <th className="w-10 border-l border-seexr-primary/20 dark:border-seexr-primary/20" />}
                   </tr>
                 ) : (
                   <>
                     <tr className="border-b border-seexr-primary/20 dark:border-seexr-primary/20 text-center bg-seexr-solid/5 dark:bg-seexr-solid/10">
                       {isEditing && <th rowSpan={2} className="w-10 border-r border-seexr-primary/20 dark:border-seexr-primary/20" />}
                       <th rowSpan={2} className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] w-12 shrink-0 text-gray-700 dark:text-gray-300 text-[12px] font-black">No.</th>
                       <th rowSpan={2} className="w-28 min-w-28 max-w-28 py-3 border-r border-gray-100 dark:border-[#2a2a2e] text-gray-700 dark:text-gray-300 text-[12px] font-black">사고분류</th>
                       <th rowSpan={2} className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] min-w-[220px] text-gray-700 dark:text-gray-300 text-[12px] font-black">유해·위험요인 파악</th>
                       <th colSpan={3} className="py-2.5 border-r border-seexr-primary/20 dark:border-seexr-primary/20 text-seexr-primary font-black text-[12px] tracking-wider">위험성 확인결과</th>
                       <th rowSpan={2} className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] w-[96px] min-w-[96px] max-w-[96px] text-gray-700 dark:text-gray-300 text-[12px] font-black">개선 전 위험성 수준</th>
                       <th rowSpan={2} className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] min-w-[180px] text-gray-700 dark:text-gray-300 text-[12px] font-black">개선대책</th>
                       <th rowSpan={2} className="py-3 border-r border-gray-100 dark:border-[#2a2a2e] w-[96px] min-w-[96px] max-w-[96px] text-gray-700 dark:text-gray-300 text-[12px] font-black">개선 후 위험성 수준</th>
                       <th rowSpan={2} className="py-3 min-w-[140px] text-left pl-6 text-gray-700 dark:text-gray-300 text-[12px] font-black">관련근거</th>
                       {isEditing && <th rowSpan={2} className="w-10 border-l border-seexr-primary/20 dark:border-seexr-primary/20" />}
                     </tr>
                     <tr className="border-b border-gray-200 dark:border-[#2a2a2e] text-gray-700 dark:text-gray-300 text-[11px] font-black text-center bg-seexr-solid/5 dark:bg-seexr-solid/[0.02]">
                       <th className="py-2 border-r border-gray-100 dark:border-[#2a2a2e] w-14">적정</th>
                       <th className="py-2 border-r border-gray-100 dark:border-[#2a2a2e] w-14">보완</th>
                       <th className="py-2 border-r border-seexr-primary/20 dark:border-seexr-primary/20 w-16">해당없음</th>
                     </tr>
                   </>
                 )}
               </thead>
               <tbody className="divide-y divide-gray-100 dark:divide-[#2a2a2e]">
                  {isEditing ? (
                    editItems.map((item, idx) => {
                     const probObj = getFreqColor(item.prob_freq);
                     const sevObj = getSevColor(item.severity);
                     const riskObj = getRiskColor(item.risk_level);
                     const postProbObj = getFreqColor(item.post_improvement_prob_freq);
                     const postSevObj = getSevColor(item.post_improvement_severity);
                     const postRiskObj = getRiskColor(item.post_improvement_risk_level);
                     const isDragOver = dragOverIdx === idx;
                     const dragProps = {
                       draggable: true,
                       onDragStart: () => handleDragStart(idx),
                       onDragOver: (e: React.DragEvent) => handleDragOver(e, idx),
                       onDragEnd: handleDragEnd,
                     };
                     const trClass = `transition-colors group ${isDragOver ? 'bg-seexr-solid/10 dark:bg-seexr-solid/20' : 'hover:bg-gray-50/50 dark:hover:bg-[#1f1f22]/50'}`;
                     const dragHandle = (
                       <td className="py-2 px-1 border-r border-gray-100 dark:border-[#2a2a2e] align-middle bg-seexr-solid/5 dark:bg-seexr-solid/10 cursor-grab active:cursor-grabbing">
                         <div className="flex items-center justify-center text-seexr-primary" title="드래그하여 순서 변경">
                           <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" opacity="0.7"><circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>
                         </div>
                       </td>
                     );
                     const deleteBtn = (
                       <td className="py-2 px-1 border-l border-gray-100 dark:border-[#2a2a2e] align-middle bg-red-50/60 dark:bg-red-950/20">
                         <div className="flex items-center justify-center">
                           <button type="button" onClick={() => deleteItem(idx)} className="p-1.5 rounded-md bg-red-100 dark:bg-red-900/40 text-red-500 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/50 hover:scale-110 transition-all" title="행 삭제">
                             <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                           </button>
                         </div>
                       </td>
                     );

                     if (riskMethodType === '빈도강도법') {
                       return (
                        <tr key={`edit-${idx}`} {...dragProps} className={trClass}>
                           {dragHandle}
                           <td className="py-3 px-1 text-xs font-bold text-gray-500 dark:text-gray-400 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/50 dark:bg-black/20">{item.id}</td>
                           <td className="py-3 px-2 text-[12px] font-bold text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/50 dark:bg-black/20">
                              <textarea className="w-full bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded p-1 outline-none focus:border-seexr-primary text-center resize-none" value={item.hazard_primary_category} onChange={e => updateEditItem(idx, 'hazard_primary_category', e.target.value)} placeholder="공종분류" />
                           </td>
                           <td className="py-3 px-2 text-[12px] font-bold text-gray-800 dark:text-gray-200 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/30 dark:bg-black/10">
                              <textarea className="w-full bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded p-1 outline-none focus:border-seexr-primary text-center resize-none" value={item.risk_category} onChange={e => updateEditItem(idx, 'risk_category', e.target.value)} placeholder="단위작업" />
                           </td>
                           <td className="w-28 min-w-28 max-w-28 py-3 px-1 text-[12px] font-bold text-gray-800 dark:text-gray-200 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/30 dark:bg-black/10">
                              <select aria-label="사고분류" className="w-full min-w-0 bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded p-1 outline-none focus:border-seexr-primary text-center" value={item.accident_classification} onChange={e => updateEditItem(idx, 'accident_classification', e.target.value)}>
                                <option value="">-</option>
                                {RISK_ASSESSMENT_ACCIDENT_CLASSIFICATIONS.map(value => <option key={value} value={value}>{value}</option>)}
                              </select>
                           </td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-900 dark:text-gray-100 border-r border-gray-100 dark:border-[#2a2a2e] align-top whitespace-pre-wrap leading-relaxed">
                              <textarea className="w-full min-h-[60px] bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded p-2 outline-none focus:border-seexr-primary resize-y" value={item.hazard} onChange={e => updateEditItem(idx, 'hazard', e.target.value)} placeholder="위험요인" />
                           </td>
                           <td className={`py-3 px-1 text-[15px] font-black ${probObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${probObj.bg}`}>
                              <input type="number" min={1} max={riskMethod === '5x4' ? 5 : riskMethod === '4x3' ? 4 : 3} className="w-[40px] text-center bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded outline-none focus:border-seexr-primary py-1" value={item.prob_freq} onChange={e => updateEditItem(idx, 'prob_freq', e.target.value)} placeholder="빈도" />
                           </td>
                           <td className={`py-3 px-1 text-[15px] font-black ${sevObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${sevObj.bg}`}>
                              <input type="number" min={1} max={riskMethod === '5x4' ? 4 : 3} className="w-[40px] text-center bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded outline-none focus:border-seexr-primary py-1" value={item.severity} onChange={e => updateEditItem(idx, 'severity', e.target.value)} placeholder="강도" />
                           </td>
                           <td className={`py-3 px-1 text-[15px] font-black ${riskObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${riskObj.bg}`}>
                              <input type="text" className="w-[40px] text-center bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded outline-none focus:border-seexr-primary py-1" value={item.risk_level} onChange={e => updateEditItem(idx, 'risk_level', e.target.value)} placeholder="등급" />
                           </td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-[#2a2a2e] align-top">
                              <BulletListEditor items={item.reduction_measures_detail} onChange={v => setEditItems(prev => prev.map((it, i) => i === idx ? { ...it, reduction_measures_detail: v } : it))} />
                           </td>
                           <td className={`py-3 px-1 text-[15px] font-black ${postProbObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${postProbObj.bg}`}>
                              <input type="number" min={1} max={riskMethod === '5x4' ? 5 : riskMethod === '4x3' ? 4 : 3} className="w-[40px] text-center bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded outline-none focus:border-seexr-primary py-1" value={item.post_improvement_prob_freq} onChange={e => updateEditItem(idx, 'post_improvement_prob_freq', e.target.value)} placeholder="-" />
                           </td>
                           <td className={`py-3 px-1 text-[15px] font-black ${postSevObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${postSevObj.bg}`}>
                              <input type="number" min={1} max={riskMethod === '5x4' ? 4 : 3} className="w-[40px] text-center bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded outline-none focus:border-seexr-primary py-1" value={item.post_improvement_severity} onChange={e => updateEditItem(idx, 'post_improvement_severity', e.target.value)} placeholder="-" />
                           </td>
                           <td className={`py-3 px-1 text-[15px] font-black ${postRiskObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${postRiskObj.bg}`}>
                              <input type="text" className="w-[40px] text-center bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded outline-none focus:border-seexr-primary py-1" value={item.post_improvement_risk_level} onChange={e => updateEditItem(idx, 'post_improvement_risk_level', e.target.value)} placeholder="-" />
                           </td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-500 dark:text-gray-400 align-top">
                              <textarea className="w-full min-h-[60px] bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded p-2 outline-none focus:border-seexr-primary resize-y" value={item.basis} onChange={e => updateEditItem(idx, 'basis', e.target.value)} placeholder="관련근거" />
                           </td>
                           {deleteBtn}
                        </tr>
                       );
                     } else if (riskMethodType === '3단계 판단법') {
                       return (
                        <tr key={`edit-${idx}`} {...dragProps} className={trClass}>
                           {dragHandle}
                           <td className="py-3 px-1 text-xs font-bold text-gray-500 dark:text-gray-400 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/50 dark:bg-black/20">{item.id}</td>
                           <td className="w-28 min-w-28 max-w-28 py-3 px-1 text-[12px] font-bold text-gray-800 dark:text-gray-200 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/30 dark:bg-black/10">
                              <select aria-label="사고분류" className="w-full min-w-0 bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded p-1 outline-none focus:border-seexr-primary text-center" value={item.accident_classification} onChange={e => updateEditItem(idx, 'accident_classification', e.target.value)}>
                                <option value="">-</option>
                                {RISK_ASSESSMENT_ACCIDENT_CLASSIFICATIONS.map(value => <option key={value} value={value}>{value}</option>)}
                              </select>
                           </td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-900 dark:text-gray-100 border-r border-gray-100 dark:border-[#2a2a2e] align-top">
                              <textarea className="w-full min-h-[60px] bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded p-2 outline-none focus:border-seexr-primary resize-y" value={item.hazard} onChange={e => updateEditItem(idx, 'hazard', e.target.value)} placeholder="유해위험요인" />
                           </td>
                           <td className={`w-[96px] min-w-[96px] max-w-[96px] py-3 px-1 text-[15px] font-black ${riskObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${riskObj.bg}`}>
                              <select className="bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded outline-none focus:border-seexr-primary py-1 px-2 text-[13px] font-bold" value={item.risk_level} onChange={e => updateEditItem(idx, 'risk_level', e.target.value)}>
                                <option value="">-</option>
                                <option value="상">상</option>
                                <option value="중">중</option>
                                <option value="하">하</option>
                              </select>
                           </td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-[#2a2a2e] align-top">
                              <BulletListEditor items={item.reduction_measures_detail} onChange={v => setEditItems(prev => prev.map((it, i) => i === idx ? { ...it, reduction_measures_detail: v } : it))} />
                           </td>
                           <td className={`w-[96px] min-w-[96px] max-w-[96px] py-3 px-1 text-[15px] font-black ${postRiskObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${postRiskObj.bg}`}>
                              <select className="bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded outline-none focus:border-seexr-primary py-1 px-2 text-[13px] font-bold" value={item.post_improvement_risk_level} onChange={e => updateEditItem(idx, 'post_improvement_risk_level', e.target.value)}>
                                <option value="">-</option>
                                <option value="상">상</option>
                                <option value="중">중</option>
                                <option value="하">하</option>
                              </select>
                           </td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-500 dark:text-gray-400 align-top">
                              <textarea className="w-full min-h-[60px] bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded p-2 outline-none focus:border-seexr-primary resize-y" value={item.basis} onChange={e => updateEditItem(idx, 'basis', e.target.value)} placeholder="관련근거" />
                           </td>
                           {deleteBtn}
                        </tr>
                       );
                     } else {
                       const clRiskObj = getRiskColor(item.risk_level);
                       return (
                        <tr key={`edit-${idx}`} {...dragProps} className={trClass}>
                           {dragHandle}
                           <td className="py-3 px-1 text-xs font-bold text-gray-500 dark:text-gray-400 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/50 dark:bg-black/20">{item.id}</td>
                           <td className="w-28 min-w-28 max-w-28 py-3 px-1 text-[12px] font-bold text-gray-800 dark:text-gray-200 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/30 dark:bg-black/10">
                              <select aria-label="사고분류" className="w-full min-w-0 bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded p-1 outline-none focus:border-seexr-primary text-center" value={item.accident_classification} onChange={e => updateEditItem(idx, 'accident_classification', e.target.value)}>
                                <option value="">-</option>
                                {RISK_ASSESSMENT_ACCIDENT_CLASSIFICATIONS.map(value => <option key={value} value={value}>{value}</option>)}
                              </select>
                           </td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-900 dark:text-gray-100 border-r border-gray-100 dark:border-[#2a2a2e] align-top">
                              <textarea className="w-full min-h-[60px] bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded p-2 outline-none focus:border-seexr-primary resize-y" value={item.hazard} onChange={e => updateEditItem(idx, 'hazard', e.target.value)} placeholder="유해·위험요인 파악 (질문형)" />
                           </td>
                           {(['적정', '보완', '해당없음'] as const).map(v => (
                             <td key={v} className="py-3 px-1 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle">
                               <input type="checkbox" checked={item.check_result === v} onChange={() => updateEditItem(idx, 'check_result', item.check_result === v ? '' : v)} className="w-4 h-4 accent-seexr-solid cursor-pointer" />
                             </td>
                           ))}
                           <td className={`w-[96px] min-w-[96px] max-w-[96px] py-3 px-1 text-[15px] font-black ${clRiskObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${clRiskObj.bg}`}>
                              <select className="bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded outline-none focus:border-seexr-primary py-1 px-2 text-[13px] font-bold" value={item.risk_level} onChange={e => updateEditItem(idx, 'risk_level', e.target.value)}>
                                <option value="">-</option>
                                <option value="상">상</option>
                                <option value="중">중</option>
                                <option value="하">하</option>
                              </select>
                           </td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-[#2a2a2e] align-top">
                              <BulletListEditor items={item.reduction_measures_detail} onChange={v => setEditItems(prev => prev.map((it, i) => i === idx ? { ...it, reduction_measures_detail: v } : it))} />
                           </td>
                           <td className={`w-[96px] min-w-[96px] max-w-[96px] py-3 px-1 text-[15px] font-black ${postRiskObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${postRiskObj.bg}`}>
                              <select className="bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded outline-none focus:border-seexr-primary py-1 px-2 text-[13px] font-bold" value={item.post_improvement_risk_level} onChange={e => updateEditItem(idx, 'post_improvement_risk_level', e.target.value)}>
                                <option value="">-</option>
                                <option value="상">상</option>
                                <option value="중">중</option>
                                <option value="하">하</option>
                              </select>
                           </td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-500 dark:text-gray-400 align-top">
                              <textarea className="w-full min-h-[60px] bg-white/50 dark:bg-black/30 border border-gray-200 dark:border-gray-700 rounded p-2 outline-none focus:border-seexr-primary resize-y" value={item.basis} onChange={e => updateEditItem(idx, 'basis', e.target.value)} placeholder="관련근거" />
                           </td>
                           {deleteBtn}
                        </tr>
                       );
                     }
                    })
                  ) : (
                    items.map((itemStr, idx) => {
                     const { extract: e, extractAll: ea } = parseXmlString(itemStr);
                     const id = e('id', itemStr) || (idx + 1).toString();
                     const accidentClassification = e('accident_classification', itemStr) || "-";
                     const hazard = e('hazard', itemStr) || e('hazard_analysis', itemStr);
                     const reduction = e('reduction_measures_detail', itemStr);
                     const basis = e('basis', itemStr) || "-";
                     const legalBasisDetailsBlock = e('legal_basis_details', itemStr);
                     const legalVerificationStatus = legalBasisDetailsBlock
                       ? e('verification_status', legalBasisDetailsBlock)
                       : '';
                     const legalBasisLaws: LegalBasisLawDetail[] = legalBasisDetailsBlock
                       ? ea('law', legalBasisDetailsBlock).map(law => ({
                           lawName: decodeXmlText(e('law_name', law)),
                           lawType: decodeXmlText(e('law_type', law)),
                           ministry: decodeXmlText(e('ministry', law)),
                           enforcementDate: decodeXmlText(e('enforcement_date', law)),
                           articleNumber: decodeXmlText(e('article_number', law)),
                           articleTitle: decodeXmlText(e('article_title', law)),
                           articleContent: decodeXmlText(e('article_content', law)),
                           contentTruncated: e('content_truncated', law) === 'true',
                         }))
                       : [];
                     const unresolvedLegalReferences = legalBasisDetailsBlock
                       ? ea('unresolved_reference', legalBasisDetailsBlock).map(decodeXmlText)
                       : [];
                     const statisticalBasis = e('statistical_basis', itemStr);
                     const scoreSource = e('score_source', itemStr) || 'MODEL_FALLBACK';
                     const recommendedFrequency = e('recommended_prob_freq', itemStr);
                     const recommendedSeverity = e('recommended_severity', itemStr);
                     const recommendedRisk = e('recommended_risk_level', itemStr);
                     const confidence = statisticalBasis ? e('confidence', statisticalBasis) : '';
                     const frequencyConfidence = statisticalBasis ? e('frequency_confidence', statisticalBasis) : '';
                     const severityConfidence = statisticalBasis ? e('severity_confidence', statisticalBasis) : '';
                     const sampleSize = statisticalBasis ? e('sample_size', statisticalBasis) : '';
                     const matchedScope = statisticalBasis ? e('matched_scope', statisticalBasis) : '';
                     const windowStart = statisticalBasis ? e('window_start', statisticalBasis) : '';
                     const windowEnd = statisticalBasis ? e('window_end', statisticalBasis) : '';
                     const caseCount = statisticalBasis ? e('case_count', statisticalBasis) : '';
                     const fatalCaseCount = statisticalBasis ? e('fatal_case_count', statisticalBasis) : '';
                     const latestAccidentAt = statisticalBasis ? e('latest_accident_at', statisticalBasis) : '';
	                     const sourceUpdatedAt = statisticalBasis ? e('source_updated_at', statisticalBasis) : '';
	                     const sourceStatus = statisticalBasis ? e('source_status', statisticalBasis) : '';
	                     const frequencyMethod = statisticalBasis ? e('frequency_method', statisticalBasis) : '';
	                     const relativePrevalencePercentile = statisticalBasis ? e('relative_prevalence_percentile', statisticalBasis) : '';
	                     const smoothedRelativePrevalencePercentile = statisticalBasis ? e('smoothed_relative_prevalence_percentile', statisticalBasis) : '';
	                     const exposureBasis = statisticalBasis ? e('exposure_basis', statisticalBasis) : '';
	                     const severityMethod = statisticalBasis ? e('severity_method', statisticalBasis) : '';
	                     const severityMinScore = statisticalBasis ? e('severity_min', statisticalBasis) : '';
	                     const severityMaxScore = statisticalBasis ? e('severity_max', statisticalBasis) : '';
	                     const worstObservedSeverity = statisticalBasis ? e('worst_observed_severity', statisticalBasis) : '';
	                     const fatalRate = statisticalBasis ? e('fatal_rate', statisticalBasis) : '';
	                     const smoothedFatalRate = statisticalBasis ? e('smoothed_fatal_rate', statisticalBasis) : '';
	                     const warnings = statisticalBasis
	                       ? ea('warning', e('warnings', statisticalBasis)).filter(Boolean)
	                       : [];
	                     const needsAttention = warnings.some(warning =>
	                       RISK_EVIDENCE_ATTENTION_WARNINGS.has(warning),
	                     );
	                     const itemWarnings = warnings.filter(
	                       warning => warning !== 'SOURCE_DATA_DEGRADED',
	                     );
	                     const reviewReasons = [...new Set([
	                       ...(sourceStatus === 'DEGRADED' && itemWarnings.length === 0
	                         ? [RISK_EVIDENCE_WARNING_REASONS.SOURCE_DATA_DEGRADED]
	                         : []),
	                       ...itemWarnings.map(riskEvidenceWarningReason),
	                     ])];
	                     const scoreSourceLabel = scoreSource === 'USER_CONFIRMED'
	                       ? '사용자 확정'
	                       : scoreSource === 'SITE_EVIDENCE'
	                         ? '현장 근거'
	                         : scoreSource === 'MIXED_SITE_CSI'
	                           ? '현장+CSI 근거'
	                           : scoreSource === 'MODEL_FALLBACK'
	                             ? '모델 추정'
	                             : 'CSI 통계 근거';
                     const evidenceTitle = statisticalBasis
                       ? [
                           `출처: ${scoreSourceLabel}`,
                           confidence && `신뢰도: ${confidence}`,
                           frequencyConfidence && `빈도 신뢰도: ${frequencyConfidence}`,
                           severityConfidence && `강도 신뢰도: ${severityConfidence}`,
                           matchedScope && `매칭: ${matchedScope}`,
                           sampleSize && `표본: ${sampleSize}`,
                           windowStart && windowEnd && `기간: ${windowStart} ~ ${windowEnd}`,
                           caseCount && `사고: ${caseCount}건`,
                           fatalCaseCount && `사망사고: ${fatalCaseCount}건`,
                           latestAccidentAt && `최신 사고: ${latestAccidentAt}`,
                           sourceUpdatedAt && `원천 수정: ${sourceUpdatedAt}`,
	                           recommendedFrequency && recommendedSeverity &&
	                             `추천: ${recommendedFrequency} × ${recommendedSeverity}${recommendedRisk ? ` = ${recommendedRisk}` : ''}`,
	                           frequencyMethod && `빈도 방법: ${friendlyEvidenceValue(frequencyMethod, RISK_EVIDENCE_METHOD_LABELS)}`,
	                           relativePrevalencePercentile && `상대 발생빈도 백분위: ${formatPercentile(relativePrevalencePercentile)}`,
	                           smoothedRelativePrevalencePercentile && `표본 보정 백분위: ${formatPercentile(smoothedRelativePrevalencePercentile)}`,
	                           exposureBasis && `노출 기준: ${friendlyEvidenceValue(exposureBasis, RISK_EVIDENCE_EXPOSURE_LABELS)}`,
	                           severityMethod && `강도 방법: ${friendlyEvidenceValue(severityMethod, RISK_EVIDENCE_METHOD_LABELS)}`,
	                           (severityMinScore || severityMaxScore) && `강도 범위: ${severityMinScore || '?'} ~ ${severityMaxScore || '?'}`,
	                           worstObservedSeverity && `최악 관측 강도: ${worstObservedSeverity}점`,
	                           fatalRate && `사망사고율: ${formatRate(fatalRate)}`,
	                           smoothedFatalRate && `표본 보정 사망사고율: ${formatRate(smoothedFatalRate)}`,
	                           warnings.length > 0 && `경고: ${warnings.join(', ')}`,
                         ].filter(Boolean).join(' · ')
                       : '';

                     if (riskMethodType === '빈도강도법') {
                       const prob = e('prob_freq', itemStr) || "-";
                       const sev = e('severity', itemStr) || "-";
                       const hazardPrimary = e('hazard_primary_category', itemStr) || "-";
                       const hazardCategory = e('risk_category', itemStr) || "-";
                       const riskLv = e('risk_level', itemStr) || "-";
                       const postProb = e('post_improvement_prob_freq', itemStr) || "-";
                       const postSev = e('post_improvement_severity', itemStr) || "-";
                       const postRiskLv = e('post_improvement_risk_level', itemStr) || "-";
                       const probObj = getFreqColor(prob);
                       const sevObj = getSevColor(sev);
                       const riskObj = getRiskColor(riskLv);
                       const postProbColor = getFreqColor(postProb);
                       const postSevColor = getSevColor(postSev);
                       const postRiskColor = getRiskColor(postRiskLv);
                       return (
                        <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-[#1f1f22]/50 transition-colors group">
                           <td className="py-3 px-1 text-xs font-bold text-gray-500 dark:text-gray-400 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/50 dark:bg-black/20">{id}</td>
                           <td className="py-3 px-2 text-[12px] font-bold text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/50 dark:bg-black/20">{hazardPrimary}</td>
                           <td className="py-3 px-2 text-[12px] font-bold text-gray-800 dark:text-gray-200 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/30 dark:bg-black/10">{hazardCategory}</td>
                           <td className="w-28 min-w-28 max-w-28 py-3 px-1 text-[12px] font-bold leading-tight text-gray-800 dark:text-gray-200 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/30 dark:bg-black/10 break-words">{accidentClassification}</td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-900 dark:text-gray-100 border-r border-gray-100 dark:border-[#2a2a2e] align-top whitespace-pre-wrap leading-relaxed">
                             {bulletLines(hazard)}
                             {statisticalBasis && (
	                               <RiskEvidenceIndicator
	                                 needsAttention={needsAttention}
	                                 ariaLabel={`${evidenceTitle}${needsAttention ? ` · 현장 확인 권장: ${reviewReasons.join(' ')}` : ''}`}
	                                 scoreSourceLabel={scoreSourceLabel}
	                                 recommendedFrequency={recommendedFrequency}
	                                 recommendedSeverity={recommendedSeverity}
	                                 recommendedRisk={recommendedRisk}
	                                 sampleSize={sampleSize}
	                                 confidence={confidence}
	                                 matchedScope={matchedScope}
	                                 windowStart={windowStart}
	                                 windowEnd={windowEnd}
	                                 frequencyMethod={frequencyMethod}
	                                 relativePrevalencePercentile={relativePrevalencePercentile}
	                                 smoothedRelativePrevalencePercentile={smoothedRelativePrevalencePercentile}
	                                 exposureBasis={exposureBasis}
	                                 severityMethod={severityMethod}
	                                 severityMinScore={severityMinScore}
	                                 severityMaxScore={severityMaxScore}
	                                 worstObservedSeverity={worstObservedSeverity}
	                                 fatalRate={fatalRate}
	                                 smoothedFatalRate={smoothedFatalRate}
	                                 reviewReasons={reviewReasons}
	                               />
                             )}
                             {renderRiskEducationAction?.(idx)}
                           </td>
                           <td className={`py-3 px-1 text-[15px] font-black ${probObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${probObj.bg}`}>{prob}</td>
                           <td className={`py-3 px-1 text-[15px] font-black ${sevObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${sevObj.bg}`}>{sev}</td>
                           <td className={`py-3 px-1 text-[15px] font-black ${riskObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${riskObj.bg}`}>{riskLv}</td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-[#2a2a2e] align-top whitespace-pre-wrap leading-relaxed">{bulletLines(reduction)}</td>
                           <td className={`py-3 px-1 text-[15px] font-black ${postProbColor.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${postProbColor.bg}`}>{postProb}</td>
                           <td className={`py-3 px-1 text-[15px] font-black ${postSevColor.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${postSevColor.bg}`}>{postSev}</td>
                           <td className={`py-3 px-1 text-[15px] font-black ${postRiskColor.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${postRiskColor.bg}`}>{postRiskLv}</td>
                           <td className="py-3 px-4 text-[12px] font-medium text-gray-500 dark:text-gray-400 align-top leading-relaxed">
                             <LegalBasisCell basis={basis} verificationStatus={legalVerificationStatus} laws={legalBasisLaws} unresolvedReferences={unresolvedLegalReferences} />
                           </td>
                        </tr>
                       );
                     } else if (riskMethodType === '3단계 판단법') {
                       const riskLv = e('risk_level_verbal', itemStr) || "-";
                       const postRiskLv = e('post_improvement_risk_level_verbal', itemStr) || "-";
                       const riskObj = getRiskColor(riskLv);
                       const postRiskObj = getRiskColor(postRiskLv);
                       return (
                        <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-[#1f1f22]/50 transition-colors group">
                           <td className="py-3 px-1 text-xs font-bold text-gray-500 dark:text-gray-400 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/50 dark:bg-black/20">{id}</td>
                           <td className="w-28 min-w-28 max-w-28 py-3 px-1 text-[12px] font-bold leading-tight text-gray-800 dark:text-gray-200 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/30 dark:bg-black/10 break-words">{accidentClassification}</td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-900 dark:text-gray-100 border-r border-gray-100 dark:border-[#2a2a2e] align-top whitespace-pre-wrap leading-relaxed">
                             {bulletLines(hazard)}
                             {renderRiskEducationAction?.(idx)}
                           </td>
                           <td className={`w-[96px] min-w-[96px] max-w-[96px] py-3 px-2 text-[15px] font-black ${riskObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${riskObj.bg}`}>{riskLv}</td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-[#2a2a2e] align-top whitespace-pre-wrap leading-relaxed">{bulletLines(reduction)}</td>
                           <td className={`w-[96px] min-w-[96px] max-w-[96px] py-3 px-2 text-[15px] font-black ${postRiskObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${postRiskObj.bg}`}>{postRiskLv}</td>
                           <td className="py-3 px-4 text-[12px] font-medium text-gray-500 dark:text-gray-400 align-top leading-relaxed">
                             <LegalBasisCell basis={basis} verificationStatus={legalVerificationStatus} laws={legalBasisLaws} unresolvedReferences={unresolvedLegalReferences} />
                           </td>
                        </tr>
                       );
                     } else {
                       const riskLv = e('risk_level_verbal', itemStr) || "-";
                       const postRiskLv = e('post_improvement_risk_level_verbal', itemStr) || "-";
                       const checkResult = e('check_result', itemStr) || '';
                       const riskObj = getRiskColor(riskLv);
                       const postRiskObj = getRiskColor(postRiskLv);
                       return (
                        <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-[#1f1f22]/50 transition-colors group">
                           <td className="py-3 px-1 text-xs font-bold text-gray-500 dark:text-gray-400 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/50 dark:bg-black/20">{id}</td>
                           <td className="w-28 min-w-28 max-w-28 py-3 px-1 text-[12px] font-bold leading-tight text-gray-800 dark:text-gray-200 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-top bg-gray-50/30 dark:bg-black/10 break-words">{accidentClassification}</td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-900 dark:text-gray-100 border-r border-gray-100 dark:border-[#2a2a2e] align-top whitespace-pre-wrap leading-relaxed">
                             {bulletLines(hazard)}
                             {renderRiskEducationAction?.(idx)}
                           </td>
                           {(['적정', '보완', '해당없음'] as const).map(v => (
                             <td key={v} className="py-3 px-1 border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle">
                               {checkResult === v && <span className="text-seexr-primary text-lg font-bold">✓</span>}
                             </td>
                           ))}
                           <td className={`w-[96px] min-w-[96px] max-w-[96px] py-3 px-2 text-[15px] font-black ${riskObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${riskObj.bg}`}>{riskLv}</td>
                           <td className="py-3 px-4 text-[13px] font-medium text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-[#2a2a2e] align-top whitespace-pre-wrap leading-relaxed">{bulletLines(reduction)}</td>
                           <td className={`w-[96px] min-w-[96px] max-w-[96px] py-3 px-2 text-[15px] font-black ${postRiskObj.text} border-r border-gray-100 dark:border-[#2a2a2e] text-center align-middle ${postRiskObj.bg}`}>{postRiskLv}</td>
                           <td className="py-3 px-4 text-[12px] font-medium text-gray-500 dark:text-gray-400 align-top leading-relaxed">
                             <LegalBasisCell basis={basis} verificationStatus={legalVerificationStatus} laws={legalBasisLaws} unresolvedReferences={unresolvedLegalReferences} />
                           </td>
                        </tr>
                       );
                     }
                    })
                  )}
               </tbody>
             </table>
             {isEditing && (
               <button type="button" onClick={addItem} className="w-full py-3.5 border-t-2 border-dashed border-seexr-300 dark:border-seexr-700 text-sm font-black text-seexr-600 dark:text-seexr-400 bg-seexr-50/80 dark:bg-seexr-950/30 hover:bg-seexr-100 dark:hover:bg-seexr-900/40 hover:border-seexr-400 dark:hover:border-seexr-500 transition-all flex items-center justify-center gap-2">
                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                 새 행 추가
               </button>
             )}
           </div>
        </div>
      </form>
    );
  };


  return (
    <>
      {projectSourceReferences.length > 0 && (
        <section className="mb-4 rounded-2xl border border-seexr-200/70 bg-seexr-50/45 p-3.5 dark:border-seexr-900/50 dark:bg-seexr-950/20" aria-label="참조한 현장 자료">
          <div className="mb-2.5 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-[12px] font-black text-gray-800 dark:text-gray-200">참조한 현장 자료</h3>
              <p className="mt-0.5 text-[9px] font-semibold text-gray-400">이 현장에 등록된 자료 중 현재 평가와 관련된 부분을 AI가 자동으로 참조했습니다.</p>
            </div>
            <span className="shrink-0 rounded-full bg-seexr-solid/10 px-2 py-1 text-[9px] font-black text-seexr-primary">{projectSourceReferences.length}개</span>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {projectSourceReferences.map(reference => (
              <div key={reference.fileId} className="flex min-w-0 items-center gap-2.5 rounded-xl border border-seexr-100 bg-white px-3 py-2.5 dark:border-seexr-900/45 dark:bg-[#19191b]">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-seexr-solid/10 text-seexr-primary">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <path d="M8 13h8M8 17h5" />
                  </svg>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[11px] font-bold text-gray-800 dark:text-gray-200">{reference.name}</span>
                  <span className="mt-0.5 block text-[9px] font-semibold text-gray-400">관련 근거 {reference.chunkCount}개 · 현장 컨텍스트 자동 참조</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
      {sourceDocuments.length > 0 && (
        <section className="mb-4 rounded-2xl border border-gray-200 bg-gray-50/70 p-3.5 dark:border-[#2a2a2e] dark:bg-[#161618]" aria-label="참고 문서">
          <div className="mb-2.5 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-[12px] font-black text-gray-800 dark:text-gray-200">참고 문서</h3>
              <p className="mt-0.5 text-[9px] font-semibold text-gray-400">분석 맥락으로만 사용되며 아티팩트 내보내기에는 포함되지 않습니다.</p>
            </div>
            <span className="shrink-0 rounded-full bg-seexr-solid/10 px-2 py-1 text-[9px] font-black text-seexr-primary">{sourceDocuments.length}개</span>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {sourceDocuments.map(documentFile => <SourceDocumentCard key={documentFile.fileId} documentFile={documentFile} />)}
          </div>
        </section>
      )}
      {renderRiskAssessment()}
    </>
  );
}
